import { Controller } from "@hotwired/stimulus"
import * as questions from "../libs/curriculum_questions"
import { enter, reducedMotion } from "../libs/motion"

// The curriculum questions stacked in the filters modal, Airbnb
// style. Each appears once the one above is answered, and one with a single
// choice is answered for the teacher and skipped, the same as the stepper.
// Picking another curriculum loads its questions in place of these.
export default class extends Controller {
  static targets = ["step", "group", "groupTitle", "more", "hint", "hintText"]
  static values = { url: String }

  connect() {
    this.reveal()
    this.followYears()
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // One answer per question, whether its choices are chips or radio rows.
  choose({ target }) {
    const step = target.closest("[data-curriculum-sections-target='step']")
    if (target.tagName !== "INPUT" || !step) return

    if (target.checked) step.querySelectorAll("input:checked").forEach(input => { if (input !== target) input.checked = false })
    if (target.checked && step.dataset.key === "curriculum" && target.value !== this.element.dataset.curriculum) return this.load(target.value)
    if (target.checked && step.dataset.key === "stage") this.hint(questions.removeClashingYears(this.form, target))

    this.reveal()
  }

  cleared() {
    this.hint(null)
    this.reveal()
  }

  // The Year level filter, in another tab of the same modal.
  yearsChanged({ target, detail }) {
    if (target.name !== "year_level[]" || detail?.fromStage) return

    this.followYears()
  }

  followYears() {
    const step = this.stepTargets.find(step => step.dataset.key === "stage")
    const stages = step ? questions.visibleOptions(step, this.groupTargets) : []
    if (!stages.length) return

    const { hint, changed } = questions.followYears(this.form, stages)
    this.hint(hint)
    if (changed) {
      this.reveal()
      this.element.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  hint(text) {
    this.hintTarget.classList.toggle("hidden", !text)
    this.hintTarget.classList.toggle("flex", !!text)
    this.hintTextTarget.textContent = text || ""
  }

  get form() {
    return this.element.closest("form")
  }

  // Shows the questions down to the first one still to answer. One that
  // appears rises in like the stepper's next step, and comes into view.
  reveal() {
    questions.cascade(this.element, this.groupTargets)
    let open = true
    const appearing = []
    this.stepTargets.forEach(step => {
      const options = questions.visibleOptions(step, this.groupTargets)
      const single = step.dataset.key !== "curriculum" && options.length === 1
      if (open && single && !options[0].checked) {
        options[0].checked = true
        questions.cascade(this.element, this.groupTargets)
      }

      const shown = open && !single && options.length > 0
      if (shown && step.classList.contains("hidden") && step.dataset.key !== "curriculum") appearing.push(step)
      step.classList.toggle("hidden", !shown)
      step.classList.toggle("flex", shown)
      if (shown && !step.querySelector("input:checked")) open = false
    })

    // A group's title only helps tell several apart.
    this.groupTitleTargets.forEach(title => {
      const step = title.closest("[data-curriculum-sections-target='step']")
      const groups = this.groupTargets.filter(group => step.contains(group) && !group.classList.contains("hidden"))
      title.classList.toggle("hidden", groups.length < 2)
    })
    questions.revealPicked(this.moreTargets)
    questions.updateMoreButtons(this.moreTargets)
    this.introduce(appearing)
  }

  introduce(steps) {
    if (!steps.length) return

    steps.at(-1).scrollIntoView({ block: "nearest", behavior: reducedMotion() ? "auto" : "smooth" })
    if (!reducedMotion()) steps.forEach(step => enter(step, "translateY(16px)"))
  }

  toggleMore({ currentTarget: button }) {
    questions.toggleMore(this.moreTargets.filter(row => button.parentElement.contains(row)))
    questions.updateMoreButtons(this.moreTargets)
  }

  load(curriculum) {
    const url = new URL(this.urlValue, location.origin)
    url.searchParams.set("curriculum", curriculum)
    this.element.closest("turbo-frame").src = url.href
  }
}
